import React from "react";
import LabelValuePair from "../common/LabelValuePair";
import { AuditLog, AuditLogTypeLabels } from "../../models/AuditLog";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import { dialogNarrow } from "@/lib/dialogSize";

/** The download's summary as the server stores it, with PascalCase keys. */
interface ExportSummary {
  /** "run" for a past run's CSV, "preview" for a preview's. */
  Source?: string;
  RuleName?: string | null;
  RowCount?: number;
}

/**
 * One CSV downloaded from a conflict rule: a past run's trail, or a preview computed when it was downloaded. The entry
 * records which, the rule's name and how many rows the file held, never the rows themselves.
 */
export const ViewConflictRuleRunExportAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
}> = ({ auditLog, open, onClose }) => {
  const data = (() => {
    if (!auditLog?.additionalData) return null;
    try {
      return JSON.parse(auditLog.additionalData) as ExportSummary;
    } catch {
      return null;
    }
  })();

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className={`${dialogNarrow} overflow-hidden`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>{AuditLogTypeLabels[auditLog.eventType]}</DialogTitle>
            </DialogHeader>

            <Separator />

            <div className="flex flex-col gap-2 py-2">
              {data ? (
                <>
                  <LabelValuePair
                    label="Downloaded"
                    value={data.Source === "preview" ? "A preview, computed when it was downloaded" : "A run's trail"}
                  />
                  <LabelValuePair label="Rule" value={data.RuleName || "Unnamed rule"} />
                  <LabelValuePair label="Rows" value={(data.RowCount ?? 0).toLocaleString()} />
                  {data.Source !== "preview" && auditLog.entityId && (
                    <LabelValuePair label="Run entry" value={auditLog.entityId} />
                  )}
                </>
              ) : (
                <span className="text-sm">Failed to parse additionalData</span>
              )}
              <LabelValuePair label="Performed by" value={auditLog.performerEmailAddress} />
              <LabelValuePair label="Event date" value={auditLog.eventDate} />
            </div>
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewConflictRuleRunExportAuditLogDetailsModal;
